import React, { useState } from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Link } from 'react-router-dom';
import { Table, Space, DatePicker, Input } from 'antd';
import type { Dayjs } from 'dayjs';

// NOTE: this local shape mirrors the API's AdminSleepStepsDayDto, matching how
// the referrals resource declares its own row type.
interface SleepStepsDay {
  id: string;
  userId: string;
  displayName: string;
  date: string;
  sleepMinutes: number;
  sleepSessions: number;
  steps: number;
}

/** 520 → "8h 40m", the way the app's Sleep card prints it. */
const formatMinutes = (minutes: number) => {
  if (!minutes) return '—';
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const SleepStepsList: React.FC = () => {
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const { tableProps } = useTable<SleepStepsDay>({
    resource: 'sleep-steps',
    filters: {
      permanent: [
        ...(userId ? [{ field: 'userId', operator: 'eq' as const, value: userId }] : []),
        ...(range?.[0] ? [{ field: 'from', operator: 'eq' as const, value: range[0].format('YYYY-MM-DD') }] : []),
        ...(range?.[1] ? [{ field: 'to', operator: 'eq' as const, value: range[1].format('YYYY-MM-DD') }] : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="sleep-steps">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          placeholder="User ID"
          allowClear
          style={{ width: 320 }}
          onSearch={(value) => setUserId(value || undefined)}
        />
        <DatePicker.RangePicker onChange={(value) => setRange(value as [Dayjs | null, Dayjs | null])} />
        <span style={{ fontSize: 12, opacity: 0.65 }}>Days are UTC, not the user&apos;s local day.</span>
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 900 }}>
        <Table.Column
          title="User"
          render={(_: unknown, record: SleepStepsDay) => (
            <Space direction="vertical" size={0}>
              <Link to={`/users/show/${record.userId}`}>{record.displayName || record.userId}</Link>
              <span style={{ fontSize: 12, opacity: 0.65 }}>{record.userId}</span>
            </Space>
          )}
        />
        <Table.Column dataIndex="date" title="Day (UTC)" sorter defaultSortOrder="descend" />
        <Table.Column
          title="Sleep"
          render={(_: unknown, record: SleepStepsDay) => formatMinutes(record.sleepMinutes)}
        />
        <Table.Column dataIndex="sleepSessions" title="Stretches" />
        <Table.Column
          title="Steps"
          render={(_: unknown, record: SleepStepsDay) => record.steps.toLocaleString()}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: SleepStepsDay) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="sleep-steps" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
